import * as i0 from '@angular/core';
import { Pipe, Input, Directive, ContentChildren, Component, InjectionToken, PLATFORM_ID, NgModule, Injectable } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { BehaviorSubject } from 'rxjs';

class E11StringToColorPipe {
    transform(value, lightness) {
        return value ? convertToColor(value, lightness) : 'grey';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11StringToColorPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: E11StringToColorPipe, name: "stringToColor" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11StringToColorPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'stringToColor' }]
        }] });
function convertToColor(stringInput = '', lightness = '35%') {
    let stringUniqueHash = [...stringInput].reduce((acc, char) => {
        return char.charCodeAt(0) + ((acc << 5) - acc);
    }, 0);
    return `hsl(${stringUniqueHash % 360}, 95%, ${lightness})`;
}

/**
 * To use this directive in a component, you need to ensure `E11UtilModule` is imported.
 * If your component is expecting an external module to inject an e11Template into it, it is recommended to import and export the `E11UtilModule` from the module containing that component
 */
class E11Template {
    constructor(template) {
        this.template = template;
    }
    getType() {
        return this.name;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11Template, deps: [{ token: i0.TemplateRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: E11Template, selector: "[e11Template]", inputs: { type: "type", name: ["e11Template", "name"] }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11Template, decorators: [{
            type: Directive,
            args: [{
                    selector: '[e11Template]',
                    host: {},
                }]
        }], ctorParameters: () => [{ type: i0.TemplateRef }], propDecorators: { type: [{
                type: Input
            }], name: [{
                type: Input,
                args: ['e11Template']
            }] } });

/**
 * DRY abstract class to create the e11TemplateMap - this grabs all instances of an E11Template and exposes them on the e11TemplateMap attribute
 * If your component is expecting an external module to inject an e11Template into it, it is recommended to import and export the `E11UtilModule` from the module containing that component
 *
 * ! Be very careful adding to this class: it should be avoided unless strictly necessary, and in particular do not inject anything into the constructor
 *
 */
// Annotation required to get around error: NG2007: Class is using Angular features but is not decorated. Please add an explicit Angular decorator.
class E11TemplateMap {
    constructor() {
        this.e11TemplateMap = {}; // Important for nested template keys.
    }
    ngAfterContentInit() {
        this.e11Templates.forEach((e11Template) => {
            this.e11TemplateMap[e11Template.getType()] = e11Template.template;
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11TemplateMap, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11TemplateMap, selector: "ng-component", queries: [{ propertyName: "e11Templates", predicate: E11Template }], ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11TemplateMap, decorators: [{
            type: Component,
            args: [{
                    template: '',
                }]
        }], propDecorators: { e11Templates: [{
                type: ContentChildren,
                args: [E11Template]
            }] } });

const IS_BROWSER = new InjectionToken('isBrowser');

const declareExports = [E11Template, E11StringToColorPipe];
class E11UtilModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11UtilModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: E11UtilModule, declarations: [E11Template, E11StringToColorPipe], imports: [CommonModule], exports: [E11Template, E11StringToColorPipe] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11UtilModule, providers: [
            {
                provide: IS_BROWSER,
                useFactory: (platformId) => isPlatformBrowser(platformId),
                deps: [PLATFORM_ID],
            },
        ], imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11UtilModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [...declareExports],
                    providers: [
                        {
                            provide: IS_BROWSER,
                            useFactory: (platformId) => isPlatformBrowser(platformId),
                            deps: [PLATFORM_ID],
                        },
                    ],
                    exports: [...declareExports],
                }]
        }] });

/**
 * ! If scroll locking can be done within the ui component, it should be implemented as an option instead of scroll locking in the consuming app
 */
class UiScrollLockingService {
    constructor() {
        this.scrollStatusSource = new BehaviorSubject(false);
        this.scrollStatusObservable$ = this.scrollStatusSource.asObservable();
    }
    scrollLock(status) {
        this.scrollStatusSource.next(status);
        const body = document.getElementsByTagName('body')[0];
        if (status) {
            body.classList.add('scroll-lock');
        }
        else {
            body.classList.remove('scroll-lock');
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: UiScrollLockingService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: UiScrollLockingService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: UiScrollLockingService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [] });

/**
 * Generated bundle index. Do not edit.
 */

export { E11StringToColorPipe, E11Template, E11TemplateMap, E11UtilModule, IS_BROWSER, UiScrollLockingService };
